import {useCallback, useEffect, useMemo} from 'react';

import {
  useBuildCancelToken,
  usePrevious,
} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {fetchSimilarConversationsAsyncAction} from '../../../../../store/data/conversations/conversationsActions';
import {makeGetConversationHasAiCompanion} from '../../../../../store/data/conversations/conversationsSelectors';

/*
 * Hook.
 */

export function useSimilarConversations(conversation?: ConversationModel, isUserVisible?: boolean) {
  const previousNumMessages = usePrevious(conversation?.numMessages);
  const previousConversationId = usePrevious(conversation?.id);

  const dispatch = useAppDispatch();
  const {buildCancelToken} = useBuildCancelToken();

  // Make sure the conversation has an AI companion.
  const getHasAiCompanion = useMemo(
    () => makeGetConversationHasAiCompanion<void>(() => conversation),
    [conversation],
  );
  const hasAiCompanion = useAppSelector(getHasAiCompanion);

  const fetchSimilarConversationsAsync = useCallback(async () => {
    if (isUserVisible || !conversation?.id || !hasAiCompanion) {
      return;
    }

    // New conversation loaded, fetch similar conversations.
    if (previousConversationId !== conversation.id) {
      dispatch(fetchSimilarConversationsAsyncAction(conversation.id, buildCancelToken()));
    }

    // If new messages are added to the conversation, refetch the similar conversations.
    if (
      previousNumMessages !== null &&
      previousNumMessages !== conversation.numMessages &&
      previousConversationId === conversation.id
    ) {
      dispatch(fetchSimilarConversationsAsyncAction(conversation.id, buildCancelToken()));
    }
  }, [
    hasAiCompanion,
    previousConversationId,
    conversation?.id,
    conversation?.numMessages,
    previousNumMessages,
    isUserVisible,
    buildCancelToken,
    dispatch,
  ]);

  useEffect(() => {
    fetchSimilarConversationsAsync();
  }, [fetchSimilarConversationsAsync]);

  const {asyncStatus, conversationIds = []} = (() => {
    if (!hasAiCompanion) {
      return {
        asyncStatus: AsyncStatusesEnum.LOADED,
        conversationIds: [],
      };
    }
    return (
      conversation?.similarConversations || {
        asyncStatus: AsyncStatusesEnum.LOADING,
        conversationIds: [],
      }
    );
  })();

  const isEmpty = asyncStatus === AsyncStatusesEnum.LOADED && conversationIds.length === 0;
  const isErred = asyncStatus === AsyncStatusesEnum.FAILED;
  const isLoaded = asyncStatus === AsyncStatusesEnum.LOADED || conversationIds.length > 0;
  const isLoading = asyncStatus === AsyncStatusesEnum.LOADING && conversationIds.length === 0;

  return {
    asyncStatus,
    conversationIds,
    isEmpty,
    isErred,
    isLoaded,
    isLoading,
    fetchSimilarConversationsAsync,
  };
}
